import { useMemo } from 'react';

// Complete chart color palette based on Figma design system
export const CHART_COLOR_PALETTE = [
  '#093575', // Dark blue
  '#0B6EFB', // Medium blue
  '#4B8EFF', // Light blue
  '#78ABFF', // Very light blue
  '#096D75', // Teal
  '#988F80', // Warm gray
  '#AE633D', // Brown
  '#80A5CB', // Grayish blue
  '#793B83', // Purple
  '#D99F75', // Peach
  '#8A1B12', // Dark red
  '#756709', // Olive green
  '#8E9841', // Lime green
  '#115BCA', // Royal blue
  '#067647', // Green
  '#3F3244', // Purple gray
  '#70707B', // Neutral gray
  '#CC7707', // Orange
  '#2C7D7F', // Dark teal
  '#ACA6AB', // Pink gray
  '#C4A69F', // Pinkish beige
  '#A3B1C2', // Blue gray
  '#7A7563', // Gray brown
  '#E5CA67', // Yellow
];

// Blue color indices in the palette (guaranteed to always include at least one)
export const BLUE_COLOR_INDICES = [0]; // Indices of blue tones

/**
 * Converts a hex color to RGB
 */
const hexToRgb = (hex: string): { r: number; g: number; b: number } => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : { r: 0, g: 0, b: 0 };
};

/**
 * Converts RGB to LAB (more perceptually uniform color space)
 */
const rgbToLab = (r: number, g: number, b: number): { l: number; a: number; b: number } => {
  // Normalize RGB to [0,1]
  r = r / 255;
  g = g / 255;
  b = b / 255;

  // Apply gamma correction
  r = r > 0.04045 ? Math.pow((r + 0.055) / 1.055, 2.4) : r / 12.92;
  g = g > 0.04045 ? Math.pow((g + 0.055) / 1.055, 2.4) : g / 12.92;
  b = b > 0.04045 ? Math.pow((b + 0.055) / 1.055, 2.4) : b / 12.92;

  // Convert to XYZ
  let x = (r * 0.4124 + g * 0.3576 + b * 0.1805) / 0.95047;
  let y = (r * 0.2126 + g * 0.7152 + b * 0.0722) / 1.00000;
  let z = (r * 0.0193 + g * 0.1192 + b * 0.9505) / 1.08883;

  // Convert XYZ to LAB
  x = x > 0.008856 ? Math.pow(x, 1/3) : (7.787 * x) + 16/116;
  y = y > 0.008856 ? Math.pow(y, 1/3) : (7.787 * y) + 16/116;
  z = z > 0.008856 ? Math.pow(z, 1/3) : (7.787 * z) + 16/116;

  return {
    l: (116 * y) - 16,
    a: 500 * (x - y),
    b: 200 * (y - z)
  };
};

/**
 * Calculates Delta E distance between two colors in LAB space
 * Higher values = more different colors
 */
const calculateColorDistance = (color1: string, color2: string): number => {
  const rgb1 = hexToRgb(color1);
  const rgb2 = hexToRgb(color2);
  
  const lab1 = rgbToLab(rgb1.r, rgb1.g, rgb1.b);
  const lab2 = rgbToLab(rgb2.r, rgb2.g, rgb2.b);
  
  const deltaL = lab1.l - lab2.l;
  const deltaA = lab1.a - lab2.a;
  const deltaB = lab1.b - lab2.b;
  
  return Math.sqrt(deltaL * deltaL + deltaA * deltaA + deltaB * deltaB);
};

/**
 * Function to mix colors avoiding similar consecutive colors
 * Uses color similarity analysis based on Delta E distance in LAB space
 */
const mixColors = (colors: string[]): string[] => {
  if (colors.length <= 1) return colors;
  if (colors.length === 2) return colors;

  const result: string[] = [];
  const available = [...colors];
  
  // Start with the first color
  result.push(available.shift()!);

  while (available.length > 0) {
    const lastColor = result[result.length - 1];
    let bestIndex = 0;
    let bestDistance = 0;

    // Find the available color with the greatest distance to the last selected one
    available.forEach((color, index) => {
      const distance = calculateColorDistance(lastColor, color);
      if (distance > bestDistance) {
        bestDistance = distance;
        bestIndex = index;
      }
    });

    // Add the color with the greatest distance and remove it from available
    result.push(available.splice(bestIndex, 1)[0]);
  }

  return result;
};

/**
 * Function to mix colors with guaranteed blue inclusion and random selection
 * Ensures at least one blue color is included, fills the rest with random colors
 * @param colors Array of available colors
 * @param blueIndices Array of indices that correspond to blue colors
 * @param count Number of colors needed
 * @returns Array of colors with at least one blue
 */
const mixColorsWithBlue = (colors: string[], blueIndices: number[], count: number): string[] => {
  if (count <= 0) return [];
  if (count === 1) {
    // If only one color needed, return the first blue (dark blue)
    return [colors[blueIndices[0]]];
  }

  const result: string[] = [];
  const availableIndices = [...Array(colors.length).keys()];
  
  // Step 1: Always start with dark blue (first blue in the array)
  const darkBlueIndex = blueIndices[0]; // This should be index 0 (dark blue #093575)
  result.push(colors[darkBlueIndex]);
  availableIndices.splice(availableIndices.indexOf(darkBlueIndex), 1);
  
  // Step 2: Fill remaining positions with random colors that have acceptable distance
  const minAcceptableDistance = 50; // Minimum Delta E distance to consider colors different enough
  
  while (result.length < count && availableIndices.length > 0) {
    const lastColor = result[result.length - 1];
    
    // Find all colors that have acceptable distance from the last color
    const acceptableColors: { index: number; distance: number; arrayIndex: number }[] = [];
    
    availableIndices.forEach((availableIndex, i) => {
      const distance = calculateColorDistance(lastColor, colors[availableIndex]);
      if (distance >= minAcceptableDistance) {
        acceptableColors.push({
          index: availableIndex,
          distance: distance,
          arrayIndex: i
        });
      }
    });
    
    // If no colors meet the minimum distance, use the one with the greatest distance
    if (acceptableColors.length === 0) {
      let bestIndex = 0;
      let bestDistance = 0;
      let bestAvailableIndex = 0;

      availableIndices.forEach((availableIndex, i) => {
        const distance = calculateColorDistance(lastColor, colors[availableIndex]);
        if (distance > bestDistance) {
          bestDistance = distance;
          bestIndex = availableIndex;
          bestAvailableIndex = i;
        }
      });

      result.push(colors[bestIndex]);
      availableIndices.splice(bestAvailableIndex, 1);
    } else {
      // Randomly select from acceptable colors
      const randomChoice = acceptableColors[Math.floor(Math.random() * acceptableColors.length)];
      result.push(colors[randomChoice.index]);
      availableIndices.splice(randomChoice.arrayIndex, 1);
    }
  }

  return result;
};

/**
 * Function to get CSS custom property
 */
const getCssVar = (property: string, fallback?: string): string => {
  if (typeof window !== 'undefined' && typeof window.getComputedStyle === 'function') {
    const computedStyle = getComputedStyle(document.documentElement);
    const value = computedStyle.getPropertyValue(property).trim();
    return value || fallback || '';
  }
  return fallback || '';
};

/**
 * Hook to get chart color palette with non-consecutive similar colors
 * @param count Number of colors needed (defaults to using entire palette)
 * @param useCssVars Whether to use CSS variables or direct values (defaults to false)
 * @param ensureBlue Whether to guarantee at least one blue color (defaults to true)
 * @returns Array of mixed colors
 */
export const useChartColors = (count?: number, useCssVars = false, ensureBlue = true) => {
  return useMemo(() => {
    let baseColors = CHART_COLOR_PALETTE;

    if (useCssVars) {
      // Try to get colors from CSS variables
      baseColors = CHART_COLOR_PALETTE.map((_, index) => {
        const cssVar = `--allie-chart-color-${index + 1}`;
        return getCssVar(cssVar, CHART_COLOR_PALETTE[index]);
      });
    }

    // If no count specified, use traditional mixing approach
    if (!count) {
      return mixColors(baseColors);
    }

    if (count > 0) {
      let selectedColors: string[];
      
      if (ensureBlue) {
        // Use the algorithm that guarantees blue inclusion
        selectedColors = mixColorsWithBlue(baseColors, BLUE_COLOR_INDICES, count);
      } else {
        // Use the traditional algorithm without blue guarantee
        const mixedColors = mixColors(baseColors);
        selectedColors = mixedColors.slice(0, count);
      }
      
      // If more colors are needed than available, repeat the pattern
      if (count > selectedColors.length) {
        const repeatedColors: string[] = [];
        for (let i = 0; i < count; i++) {
          repeatedColors.push(selectedColors[i % selectedColors.length]);
        }
        return repeatedColors;
      }
      
      return selectedColors;
    }

    return mixColors(baseColors);
  }, [count, useCssVars, ensureBlue]);
};

/**
 * Hook to get a specific color from the mixed palette
 * @param index Index of the desired color
 * @param useCssVars Whether to use CSS variables or direct values (defaults to false)
 * @returns Color at the specified position
 */
export const useChartColor = (index: number, useCssVars = false) => {
  const colors = useChartColors(undefined, useCssVars);
  
  return useMemo(() => {
    return colors[index % colors.length];
  }, [colors, index]);
};

/**
 * Utility function to get colors directly (without hook)
 * @param count Number of colors needed
 * @param ensureBlue Whether to guarantee at least one blue color (defaults to true)
 * @returns Array of mixed colors
 */
export const getChartColors = (count?: number, ensureBlue = true): string[] => {
  // If no count specified, use the traditional mixing approach
  if (!count) {
    return mixColors(CHART_COLOR_PALETTE);
  }

  if (count > 0) {
    let selectedColors: string[];
    
    if (ensureBlue) {
      // Use the algorithm that guarantees blue inclusion
      selectedColors = mixColorsWithBlue(CHART_COLOR_PALETTE, BLUE_COLOR_INDICES, count);
    } else {
      // Use the traditional algorithm without blue guarantee
      const mixedColors = mixColors(CHART_COLOR_PALETTE);
      selectedColors = mixedColors.slice(0, count);
    }
    
    // If more colors are needed than available, repeat the pattern
    if (count > selectedColors.length) {
      const repeatedColors: string[] = [];
      for (let i = 0; i < count; i++) {
        repeatedColors.push(selectedColors[i % selectedColors.length]);
      }
      return repeatedColors;
    }
    
    return selectedColors;
  }

  return mixColors(CHART_COLOR_PALETTE);
};

/**
 * Utility function to get a specific color (without hook)
 * @param index Index of the desired color
 * @returns Color at the specified position
 */
export const getChartColor = (index: number): string => {
  const colors = getChartColors();
  return colors[index % colors.length];
};

/**
 * Utility function to evaluate color distribution quality
 * @param colors Array of colors to evaluate
 * @returns Object with distance statistics between consecutive colors
 */
export const analyzeColorDistribution = (colors: string[]) => {
  if (colors.length < 2) return { minDistance: 0, maxDistance: 0, avgDistance: 0, distances: [] };
  
  const distances: number[] = [];
  
  for (let i = 0; i < colors.length - 1; i++) {
    const distance = calculateColorDistance(colors[i], colors[i + 1]);
    distances.push(distance);
  }
  
  const minDistance = Math.min(...distances);
  const maxDistance = Math.max(...distances);
  const avgDistance = distances.reduce((sum, d) => sum + d, 0) / distances.length;
  
  return {
    minDistance: Math.round(minDistance * 100) / 100,
    maxDistance: Math.round(maxDistance * 100) / 100,
    avgDistance: Math.round(avgDistance * 100) / 100,
    distances: distances.map(d => Math.round(d * 100) / 100)
  };
};

// =============================================================================
// MONOCHROMATIC PALETTE FUNCTIONS
// =============================================================================

/**
 * Converts RGB to HSL
 */
const rgbToHsl = (r: number, g: number, b: number): { h: number; s: number; l: number } => {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const delta = max - min;
    s = l > 0.5 ? delta / (2 - max - min) : delta / (max + min);

    switch (max) {
      case r: h = (g - b) / delta + (g < b ? 6 : 0); break;
      case g: h = (b - r) / delta + 2; break;
      case b: h = (r - g) / delta + 4; break;
    }
    h /= 6;
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
};

/**
 * Converts HSL to RGB
 */
const hslToRgb = (h: number, s: number, l: number): { r: number; g: number; b: number } => {
  h /= 360;
  s /= 100;
  l /= 100;

  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  };

  let r, g, b;

  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }

  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255)
  };
};

/**
 * Converts RGB to hex
 */
const rgbToHex = (r: number, g: number, b: number): string => {
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
};

/**
 * Function that generates an array of interpolated monochromatic colors
 * that includes the base color and creates variations around it.
 * Similar to generateGradientColors but ensures the base color is always included.
 */
const generateMonochromaticPalette = (baseColor: string, count: number): string[] => {
  if (count <= 0) return [];
  if (count === 1) return [baseColor];

  const rgb = hexToRgb(baseColor);
  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
  
  const rangeMultiplier = count <= 4 ? 0.6 : count <= 8 ? 0.8 : 1.0;
  const lightRange = count > 10 ? 70 : 45 * rangeMultiplier; // Extended range for #B2CDFF
  const darkRange = 35 * rangeMultiplier; // More range for darker variations
  
  // Create a lighter and darker version of the base color
  // Allow colors as light as #B2CDFF (lightness ~83)
  const lighterLightness = Math.min(83, hsl.l + lightRange);
  const darkerLightness = Math.max(10, hsl.l - darkRange);
  
  // Generate variations with different saturations for more variety like Figma
  const createColorVariation = (lightness: number, saturationMultiplier: number) => {
    const adjustedSaturation = Math.min(100, Math.max(20, hsl.s * saturationMultiplier));
    const newRgb = hslToRgb(hsl.h, adjustedSaturation, lightness);
    return rgbToHex(newRgb.r, newRgb.g, newRgb.b);
  };
  
  // Generate lighter version (similar to #B2CDFF)
  const lighterHex = createColorVariation(lighterLightness, 0.7);
  
  // Generate darker version (similar to #2A4873)  
  const darkerHex = createColorVariation(darkerLightness, 1.1);
  
  // Always include the base color first
  const colors: string[] = [baseColor];
  
  if (count === 2) {
    // For 2 colors, add a lighter version for better contrast
    colors.push(lighterHex);
    return colors;
  }
  
  // For more than 2 colors, create strategic distribution
  if (count <= 4) {
    // For small counts, manually create good distribution
    const positions = [];
    if (count === 3) {
      positions.push(0.7); // Lighter
      positions.push(0.9); // Much lighter
    } else if (count === 4) {
      positions.push(0.4); // Slightly lighter  
      positions.push(0.7); // Lighter
      positions.push(0.95); // Much lighter (almost white-ish but keeping hue)
    }
    
    const startRGB = hexToRgb(darkerHex);
    const endRGB = hexToRgb(lighterHex);
    
    for (let i = 0; i < positions.length; i++) {
      const ratio = positions[i];
      const r = Math.round(startRGB.r * (1 - ratio) + endRGB.r * ratio);
      const g = Math.round(startRGB.g * (1 - ratio) + endRGB.g * ratio);
      const b = Math.round(startRGB.b * (1 - ratio) + endRGB.b * ratio);
      let interpolatedColor = rgbToHex(r, g, b);
      
      // Add saturation variation for some colors to match Figma variety
      if (i % 2 === 1 && count >= 6) {
        const interpolatedRgb = hexToRgb(interpolatedColor);
        const interpolatedHsl = rgbToHsl(interpolatedRgb.r, interpolatedRgb.g, interpolatedRgb.b);
        // Make some colors more grayish (like #80A5CB in Figma)
        const greyishSaturation = Math.max(25, interpolatedHsl.s * 0.8);
        interpolatedColor = createColorVariation(interpolatedHsl.l, greyishSaturation / hsl.s);
      }
      
      if (interpolatedColor.toLowerCase() !== baseColor.toLowerCase()) {
        colors.push(interpolatedColor);
      }
    }
  } else {
    // For more colors, use regular interpolation
    const remainingCount = count - 1;
    const startRGB = hexToRgb(lighterHex);
    const endRGB = hexToRgb(darkerHex);
    
    for (let i = 0; i < remainingCount; i++) {
      const ratio = remainingCount === 1 ? 1 : i / (remainingCount - 1);
      const r = Math.round(startRGB.r * (1 - ratio) + endRGB.r * ratio);
      const g = Math.round(startRGB.g * (1 - ratio) + endRGB.g * ratio);
      const b = Math.round(startRGB.b * (1 - ratio) + endRGB.b * ratio);
      let interpolatedColor = rgbToHex(r, g, b);
      
      // Add saturation variation for variety (like Figma's #80A5CB, #9DBBED)
      if (i % 3 === 2 && count > 8) {
        const interpolatedRgb = hexToRgb(interpolatedColor);
        const interpolatedHsl = rgbToHsl(interpolatedRgb.r, interpolatedRgb.g, interpolatedRgb.b);
        // Some colors more grayish/desaturated
        const saturationVariation = 0.7 + (i % 3) * 0.15; // 0.7, 0.85, 1.0
        interpolatedColor = createColorVariation(interpolatedHsl.l, saturationVariation);
      }
      
      if (interpolatedColor.toLowerCase() !== baseColor.toLowerCase()) {
        colors.push(interpolatedColor);
      }
    }
  }
  
  // If we don't have enough colors due to duplicates, fill with darker/lighter variations
  while (colors.length < count) {
    const lastColor = colors[colors.length - 1];
    const lastRgb = hexToRgb(lastColor);
    const lastHsl = rgbToHsl(lastRgb.r, lastRgb.g, lastRgb.b);
    
    // Make it darker if possible, otherwise lighter
    const newLightness = lastHsl.l > 20 
      ? Math.max(5, lastHsl.l - 15)
      : Math.min(95, lastHsl.l + 15);
    
    const newRgb = hslToRgb(lastHsl.h, lastHsl.s, newLightness);
    const newColor = rgbToHex(newRgb.r, newRgb.g, newRgb.b);
    
    if (!colors.includes(newColor)) {
      colors.push(newColor);
    } else {
      break; // Avoid infinite loop
    }
  }
  
  // Apply mixing to avoid consecutive similar colors while maintaining variety
  const finalColors = colors.slice(0, count);
  
  // If we have more than 2 colors, mix them to avoid consecutive similarities
  if (finalColors.length > 2) {
    return mixColors(finalColors);
  }
  
  return finalColors;
};

/**
 * Hook to get monochromatic color palette
 * @param baseColor Base color for the monochromatic palette (hex format)
 * @param count Number of colors needed
 * @param useCssVars Whether to use CSS variables or direct values (defaults to false)
 * @returns Array of monochromatic colors with good contrast between them
 */
export const useMonochromaticColors = (baseColor: string, count: number, useCssVars = false) => {
  return useMemo(() => {
    let finalBaseColor = baseColor;

    if (useCssVars) {
      // If using CSS variables, try to resolve the base color from CSS
      finalBaseColor = getCssVar(baseColor, baseColor);
    }

    return generateMonochromaticPalette(finalBaseColor, count);
  }, [baseColor, count, useCssVars]);
};

/**
 * Utility function to get monochromatic colors directly (without hook)
 * @param baseColor Base color for the monochromatic palette (hex format)
 * @param count Number of colors needed
 * @returns Array of monochromatic colors
 */
export const getMonochromaticColors = (baseColor: string, count: number): string[] => {
  return generateMonochromaticPalette(baseColor, count);
};

/**
 * Predefined monochromatic base colors from the design system
 */
export const MONOCHROMATIC_BASE_COLORS = {
  primary: '#093575', // Primary blue
  secondary: '#146ef5', // Secondary blue  
  success: '#067647', // Green
  warning: '#CC7707', // Orange
  error: '#8A1B12', // Red
  neutral: '#70707B', // Gray
  purple: '#793B83', // Purple
  teal: '#096D75', // Teal
} as const;

/**
 * Hook to get predefined monochromatic palettes
 * @param colorName Name of the predefined color (primary, secondary, success, etc.)
 * @param count Number of colors needed
 * @param useCssVars Whether to use CSS variables or direct values (defaults to false)
 * @returns Array of monochromatic colors for the specified color name
 */
export const useMonochromaticPalette = (
  colorName: keyof typeof MONOCHROMATIC_BASE_COLORS, 
  count: number, 
  useCssVars = false
) => {
  return useMonochromaticColors(MONOCHROMATIC_BASE_COLORS[colorName], count, useCssVars);
};

/**
 * Utility function to get predefined monochromatic palettes (without hook)
 * @param colorName Name of the predefined color
 * @param count Number of colors needed
 * @returns Array of monochromatic colors
 */
export const getMonochromaticPalette = (
  colorName: keyof typeof MONOCHROMATIC_BASE_COLORS, 
  count: number
): string[] => {
  return getMonochromaticColors(MONOCHROMATIC_BASE_COLORS[colorName], count);
};
